import { authRepo } from "@/lib/repositories";
import type { LoginCredentials } from "../types";
import { useCallback, useMemo } from "react";
import { catchAxiosError } from "@/lib/errors";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "@/stores/useAuthStore";
import { ROUTES } from "@/constants/routes";
import { useNavigate } from "react-router-dom";
import { queryClient } from "@/lib/query-client";
import type { ApiUser } from "@/features/users/types";
import { setUser as setSentryUser, clearUser } from "@/lib/sentry";
import { AuthorizedUserGroups } from "../constants/groups";

const useAuth = () => {
  const { t } = useTranslation("modules.auth");
  const { clear, setToken, setRefreshToken, setUser, token, user } =
    useAuthStore((state) => state);
  const navigate = useNavigate();

  const updateAuthStore = useCallback(
    async (
      authToken: string | null,
      refreshToken: string | null = null,
      userData?: ApiUser,
    ) => {
      if (authToken === null) {
        clear();
        clearUser();
      } else {
        setToken(authToken);
        setRefreshToken(refreshToken);
        setUser(JSON.stringify(userData));
        if (userData) {
          setSentryUser({
            id: userData?.id.toString(),
            email: userData?.email,
            username: userData?.username,
          });
        }
      }
    },
    [clear, setToken, setRefreshToken, setUser],
  );

  const hasAuthorizedGroup = useMemo(
    () => (userData: ApiUser) =>
      userData.groups.some((group) =>
        AuthorizedUserGroups.includes(group.name),
      ),
    [],
  );

  const handleUserAuthentication = useCallback(
    async (authToken: string, refreshToken: string) => {
      const [userError, userData] = await catchAxiosError(authRepo.loadUser());

      if (userError) {
        throw new Error(t("user_load_failed"));
      }

      if (!hasAuthorizedGroup(userData)) {
        setToken(null);
        setRefreshToken(null);
        throw new Error(t("not_authorize"));
      }
      await updateAuthStore(authToken, refreshToken, userData);
    },
    [hasAuthorizedGroup, t, updateAuthStore, setToken, setRefreshToken],
  );

  const login = useCallback(
    async (credentials: LoginCredentials) => {
      const [error, authData] = await catchAxiosError(
        authRepo.login(credentials),
      );
      if (error) throw error;
      setToken(authData.access);
      setRefreshToken(authData.refresh);
      await handleUserAuthentication(authData.access, authData.refresh);
    },
    [handleUserAuthentication, setToken, setRefreshToken],
  );

  const logout = useCallback(async () => {
    try {
      // Disconnect first to unmount active components
      await updateAuthStore(null);

      // Navigate to  login screen
      navigate(ROUTES.LOGIN);

      // 4. Stop React Query and clear it cache
      queryClient.cancelQueries();
      queryClient.clear();

      localStorage.clear();
    } catch (error) {
      console.error("[LOGOUT] Error: ", error);
      navigate(ROUTES.LOGIN);
      await updateAuthStore(null);
    }
  }, [updateAuthStore, navigate]);

  return {
    login,
    logout,
    isAuthenticated: !!token && !!user,
    user: user ? JSON.parse(user) : null,
    token,
  };
};

export default useAuth;
